import { useEffect, useMemo, useRef, useState } from "react";
function detectTier() {
  const prefersReduced = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const w = typeof window === "undefined" ? 1024 : window.innerWidth;
  const mem = typeof navigator !== "undefined" ? navigator.deviceMemory : void 0;
  let quality = "high";
  if (w < 640) quality = "low";
  else if (w < 1180) quality = "medium";
  const cores = typeof navigator !== "undefined" ? navigator.hardwareConcurrency : void 0;
  if (typeof mem === "number" && mem <= 2) quality = "low";
  else if (typeof cores === "number" && cores <= 4 && quality === "high") quality = "medium";
  const dpr = quality === "low" ? 1 : quality === "medium" ? 1.25 : 1.5;
  const counts = quality === "low" ? { petalCount: 9, particleCount: 220 } : quality === "medium" ? { petalCount: 14, particleCount: 420 } : { petalCount: 20, particleCount: 700 };
  return { quality, dpr, ...counts, reducedMotion: prefersReduced };
}
let webglCache = null;
function canUseWebGL() {
  if (webglCache !== null) return webglCache;
  try {
    if (typeof document === "undefined") return webglCache = false;
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl");
    webglCache = Boolean(gl);
    if (gl && "getExtension" in gl) {
      const lose = gl.getExtension("WEBGL_lose_context");
      lose?.loseContext();
    }
    return webglCache;
  } catch {
    webglCache = false;
    return false;
  }
}
function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}
function usePointerParallax(enabled) {
  const value = useRef({ x: 0, y: 0 });
  const target = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let raf = 0;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const v = value.current;
      const t = target.current;
      v.x += (t.x - v.x) * 0.055;
      v.y += (t.y - v.y) * 0.055;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onPointer = (e) => {
      if (e.pointerType === "touch") return;
      target.current.x = e.clientX / window.innerWidth * 2 - 1;
      target.current.y = -(e.clientY / window.innerHeight * 2 - 1);
    };
    const onOrient = (e) => {
      const gamma = e.gamma ?? 0;
      const beta = e.beta ?? 0;
      target.current.x = Math.max(-1, Math.min(1, gamma / 35));
      target.current.y = Math.max(-1, Math.min(1, (beta - 45) / 40));
    };
    window.addEventListener("pointermove", onPointer, { passive: true });
    window.addEventListener("deviceorientation", onOrient, { passive: true });
    const askTilt = () => {
      const D = window.DeviceOrientationEvent;
      if (typeof D?.requestPermission === "function") {
        void D.requestPermission().catch(() => void 0);
      }
      window.removeEventListener("pointerdown", askTilt);
    };
    window.addEventListener("pointerdown", askTilt, { once: true });
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("deviceorientation", onOrient);
      window.removeEventListener("pointerdown", askTilt);
    };
  }, [enabled]);
  return value;
}
function useActiveWhenVisible(ref) {
  const [active, setActive] = useState(true);
  useEffect(() => {
    let visible = true;
    let onScreen = true;
    const sync = () => setActive(visible && onScreen);
    const onVisibility = () => {
      visible = document.visibilityState === "visible";
      sync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    let io;
    if (ref.current && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver((entries) => {
        onScreen = entries.some((e) => e.isIntersecting);
        sync();
      }, { rootMargin: "120px" });
      io.observe(ref.current);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
    };
  }, [ref]);
  return active;
}
function useSceneMode() {
  const reduced = useReducedMotion();
  const [gl, setGl] = useState(null);
  useEffect(() => {
    setGl(canUseWebGL());
  }, []);
  const tier = useMemo(() => detectTier(), []);
  const mode = gl === null || !gl ? "off" : reduced ? "static" : "live";
  return { mode, tier };
}
function disposeScene(ref) {
  ref.traverse((obj) => {
    const o = obj;
    o.geometry?.dispose();
    const mats = o.material ?? [];
    const list = Array.isArray(mats) ? mats : [mats];
    for (const m of list) {
      const mat = m;
      mat?.map?.dispose?.();
      mat?.dispose?.();
    }
  });
}
export {
  canUseWebGL,
  detectTier,
  disposeScene,
  useActiveWhenVisible,
  usePointerParallax,
  useReducedMotion,
  useSceneMode
};
